import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button, Input, Loading, Textarea } from "components/ui";
import Heading from "components/ui/Heading/Heading";
import SelectUI from "components/ui/SelectUI/SelectUI";
import { ToastContext } from "components/dashboard/common/Toast/context/ToastContextProvider";
import { apiRoute } from "services";
import API from "services/lib/api";
import { dataQueryStatus } from "utils";
import { getErrorMessage } from "utils/helper";
import {
    ACTION_OPTIONS,
    AUTOMATION_ACTION_TYPE,
    AUTOMATION_CONDITION_FIELD,
    AUTOMATION_OPERATOR,
    AUTOMATION_STATUS,
    TEMPLATE_VARIABLES,
    TICKET_STATUS_VALUES,
    defaultCondition,
    emptyAutomationForm,
} from "../enum";
import CreateAutomationHeader from "./CreateAutomationHeader/CreateAutomationHeader";
import AutomationConditionGroup from "./AutomationConditionGroup/AutomationConditionGroup";
import ComponentAccessControl from "components/essentials/ComponentAccessControl/ComponentAccessControl";
import {
    AUTOMATION_CREATE_PERMISSION,
    AUTOMATION_UPDATE_PERMISSION,
} from "../permissions";
import "./CreateAutomation.scss";

const { ERROR } = dataQueryStatus;

const findOption = (options, value) =>
    options.find((option) => option.value === value) || null;

const CreateAutomation = () => {
    const { automationId } = useParams();
    const isEdit = Boolean(automationId) && automationId !== "new";
    const history = useHistory();
    const toastMessage = useContext(ToastContext);
    const [form, setForm] = useState(emptyAutomationForm());
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    const [customTicketStatusOptions, setCustomTicketStatusOptions] = useState(
        []
    );

    useEffect(() => {
        const loadAssignableStatuses = async () => {
            try {
                const res = await API.get(
                    apiRoute.getAssignableCustomTicketStatuses
                );
                if (res.status === 200) {
                    const list = res?.data?.data || [];
                    setCustomTicketStatusOptions(
                        list.map((status) => ({
                            label: status.name,
                            value: status.customTicketStatusId,
                        }))
                    );
                }
            } catch (err) {
                // Non-blocking: existing automations still work without custom statuses.
            }
        };
        loadAssignableStatuses();
    }, []);

    useEffect(() => {
        if (!isEdit) return;

        const load = async () => {
            try {
                setLoading(true);
                const res = await API.get(apiRoute.getAutomation(automationId));
                if (res.status === 200) {
                    const data = res?.data?.data;
                    setForm({
                        name: data.name || "",
                        matchAllConditions: data.matchAllConditions?.length
                            ? data.matchAllConditions
                            : [],
                        matchAnyConditions: data.matchAnyConditions || [],
                        actionType: data.actionType,
                        actionPayload: {
                            messageTemplate:
                                data.actionPayload?.messageTemplate || "",
                            notifyCustomer:
                                data.actionPayload?.notifyCustomer !== false,
                        },
                        status: data.status || AUTOMATION_STATUS.INACTIVE,
                    });
                }
            } catch (err) {
                toastMessage(getErrorMessage(err), ERROR);
            } finally {
                setLoading(false);
            }
        };

        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [automationId]);

    const updateField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const updateCondition = (group, index, key, value) => {
        setForm((prev) => {
            const list = [...prev[group]];
            const next = { ...list[index], [key]: value };

            if (key === "field") {
                if (value === AUTOMATION_CONDITION_FIELD.TICKET_STATUS) {
                    next.operator = AUTOMATION_OPERATOR.IS;
                    next.value = TICKET_STATUS_VALUES.WAITING_FOR_CUSTOMER;
                } else if (
                    value === AUTOMATION_CONDITION_FIELD.CUSTOM_TICKET_STATUS
                ) {
                    next.operator = AUTOMATION_OPERATOR.IS;
                    next.value = customTicketStatusOptions[0]?.value || "";
                } else if (
                    value === AUTOMATION_CONDITION_FIELD.HOURS_SINCE_STATUS ||
                    value ===
                        AUTOMATION_CONDITION_FIELD.HOURS_SINCE_LAST_ACTIVITY ||
                    value ===
                        AUTOMATION_CONDITION_FIELD.HOURS_SINCE_CUSTOM_TICKET_STATUS ||
                    value ===
                        AUTOMATION_CONDITION_FIELD.HOURS_SINCE_LAST_CUSTOMER_REPLY
                ) {
                    next.operator = AUTOMATION_OPERATOR.GREATER_THAN;
                    next.value = 24;
                } else if (value === AUTOMATION_CONDITION_FIELD.NO_AGENT_REPLY) {
                    next.operator = AUTOMATION_OPERATOR.IS;
                    next.value = true;
                } else {
                    next.operator = AUTOMATION_OPERATOR.IS;
                    next.value = "";
                }
            }

            list[index] = next;
            return { ...prev, [group]: list };
        });
    };

    const addCondition = (group) => {
        setForm((prev) => ({
            ...prev,
            [group]: [...prev[group], defaultCondition()],
        }));
    };

    const removeCondition = (group, index) => {
        setForm((prev) => ({
            ...prev,
            [group]: prev[group].filter((_, i) => i !== index),
        }));
    };

    const insertVariable = (variable) => {
        setForm((prev) => ({
            ...prev,
            actionPayload: {
                ...prev.actionPayload,
                messageTemplate: `${
                    prev.actionPayload.messageTemplate || ""
                }${variable}`,
            },
        }));
    };

    const previewText = useMemo(() => {
        const template = form.actionPayload?.messageTemplate || "";
        return template.replace(
            /\{\{\s*customer\.first_name\s*\}\}/gi,
            "Ada"
        );
    }, [form.actionPayload?.messageTemplate]);

    const handleSubmit = async (activateAfterSave = false) => {
        if (!form.name?.trim()) {
            toastMessage("Please name this automation", ERROR);
            return;
        }
        if (
            !form.matchAllConditions?.length &&
            !form.matchAnyConditions?.length
        ) {
            toastMessage("Add at least one condition", ERROR);
            return;
        }
        if (
            form.actionType === AUTOMATION_ACTION_TYPE.SEND_MESSAGE &&
            !form.actionPayload?.messageTemplate?.trim()
        ) {
            toastMessage("Message template is required", ERROR);
            return;
        }

        try {
            setSaving(true);
            const payload = {
                name: form.name.trim(),
                matchAllConditions: form.matchAllConditions,
                matchAnyConditions: form.matchAnyConditions,
                actionType: form.actionType,
                actionPayload: form.actionPayload,
                status: activateAfterSave
                    ? AUTOMATION_STATUS.ACTIVE
                    : form.status,
            };

            let res;
            if (isEdit) {
                res = await API.put(
                    apiRoute.updateAutomation(automationId),
                    payload
                );
            } else {
                res = await API.post(apiRoute.createAutomation, payload);
            }

            if (res.status === 200 || res.status === 201) {
                const savedId = res?.data?.data?.automationId || automationId;
                if (
                    activateAfterSave &&
                    savedId &&
                    payload.status !== AUTOMATION_STATUS.ACTIVE
                ) {
                    await API.put(apiRoute.activateAutomation(savedId));
                }
                toastMessage(
                    activateAfterSave
                        ? "Automation activated successfully"
                        : res?.data?.message || "Automation saved successfully"
                );
                history.push("/t-sam/automations");
            }
        } catch (err) {
            toastMessage(getErrorMessage(err), ERROR);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <>
                <CreateAutomationHeader isEdit={isEdit} />
                <div className='padded__layout pt-4'>
                    <Loading />
                </div>
            </>
        );
    }

    return (
        <>
            <CreateAutomationHeader isEdit={isEdit} />
            <div className='padded__layout pt-4 create-automation'>
                <div className='create-automation__heading-row'>
                    <Heading
                        headingText={
                            isEdit ? "Edit automation" : "Create automation"
                        }
                        headingSubText='Define the conditions and action, then activate it.'
                    />
                    {isEdit && (
                        <Button
                            type='button'
                            text='Preview matching tickets'
                            classType='plain'
                            otherClass='create-automation__preview-btn'
                            onClick={() =>
                                history.push(
                                    `/t-sam/automation/${automationId}/matching-tickets`
                                )
                            }
                        />
                    )}
                </div>

                <form
                    className='mt-4 create-automation__form'
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit(true);
                    }}>
                    <Input
                        label='Name of automation'
                        labelClass='text-dark fw-medium'
                        inputClass='form-control'
                        name='name'
                        placeholder='e.g Auto close'
                        value={form.name}
                        onChange={({ target: { value } }) =>
                            updateField("name", value)
                        }
                        autoFocus={!isEdit}
                    />

                    <AutomationConditionGroup
                        title='Meet ALL of the following conditions'
                        group='matchAllConditions'
                        conditions={form.matchAllConditions}
                        onUpdate={updateCondition}
                        onAdd={addCondition}
                        onRemove={removeCondition}
                        customTicketStatusOptions={customTicketStatusOptions}
                    />

                    <AutomationConditionGroup
                        title='Meet ANY of the following conditions'
                        group='matchAnyConditions'
                        conditions={form.matchAnyConditions}
                        onUpdate={updateCondition}
                        onAdd={addCondition}
                        onRemove={removeCondition}
                        customTicketStatusOptions={customTicketStatusOptions}
                    />

                    <div className='mb-4'>
                        <SelectUI
                            name='actionType'
                            label='Pick an action'
                            darkLabel
                            placeholder='Select action'
                            options={ACTION_OPTIONS}
                            value={findOption(ACTION_OPTIONS, form.actionType)}
                            onChange={({ value }) =>
                                updateField("actionType", value)
                            }
                            isRequired
                        />
                    </div>

                    {form.actionType ===
                        AUTOMATION_ACTION_TYPE.SEND_MESSAGE && (
                        <div className='create-automation__message mb-4'>
                            <div>
                                <Textarea
                                    label='Message'
                                    labelClass='text-dark fw-medium'
                                    inputClass='form-control'
                                    name='messageTemplate'
                                    rows={6}
                                    placeholder='Hi {{customer.first_name}}...'
                                    value={
                                        form.actionPayload.messageTemplate || ""
                                    }
                                    onChange={({ target: { value } }) =>
                                        updateField("actionPayload", {
                                            ...form.actionPayload,
                                            messageTemplate: value,
                                        })
                                    }
                                />
                                <div className='create-automation__variables'>
                                    {TEMPLATE_VARIABLES.map((variable) => (
                                        <button
                                            type='button'
                                            key={variable}
                                            className='create-automation__variable-btn'
                                            onClick={() =>
                                                insertVariable(variable)
                                            }>
                                            Insert {variable}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className='create-automation__preview'>
                                <p className='create-automation__preview-label'>
                                    Preview
                                </p>
                                <p className='create-automation__preview-brand'>
                                    Metacare Support
                                </p>
                                <div className='create-automation__preview-body'>
                                    {previewText ||
                                        "Your message preview will appear here."}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className='create-automation__footer'>
                        <Button
                            type='button'
                            text='Cancel'
                            classType='plain'
                            onClick={() => history.push("/t-sam/automations")}
                        />
                        <ComponentAccessControl
                            baseResources={
                                isEdit
                                    ? AUTOMATION_UPDATE_PERMISSION
                                    : AUTOMATION_CREATE_PERMISSION
                            }>
                            <Button
                                type='submit'
                                text={
                                    saving
                                        ? "Saving..."
                                        : isEdit
                                        ? "Save & activate"
                                        : "Continue"
                                }
                                classType='primary'
                                disabled={saving}
                                loading={saving}
                            />
                        </ComponentAccessControl>
                    </div>
                </form>
            </div>
        </>
    );
};

export default CreateAutomation;
